import { useState } from 'react'

import type { Gate } from '../../../api/drama'
import { t } from '../../../i18n'

/** 封面取不到就什么都不画。破图图标比纯黑还难看，而且像是坏了。 */
function Cover({ url }: { url?: string }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) return null
  return <img src={url} alt="" style={styles.cover} onError={() => setFailed(true)} />
}

/**
 * 不在播的那一屏。
 *
 * 滑动时上下两屏要有东西可看——纯黑的话，手指一动就像是把画面拖没了。
 * 用剧封面压暗铺底，加一个集号，和在播的那一屏对得上。
 */
export function PosterSlot({ coverUrl, number }: { coverUrl?: string; number: number }) {
  return (
    <div style={styles.wrap}>
      <Cover url={coverUrl} />
      <span style={styles.number}>
        {t.episodeLabel} {number}
      </span>
    </div>
  )
}

/**
 * 锁住的那一屏。
 *
 * 门禁结论来自服务端（`episode.locked` / `episode.gate`），前端不自己判断能看几集。
 * 这里**不调播放接口**：滑过一串锁住的集会连打一串必然 403 的请求，
 * 还可能撞上限流。真正的解锁链接等用户点了按钮再去取。
 */
export function LockedSlot({
  coverUrl,
  number,
  gate,
  onUnlock,
}: {
  coverUrl?: string
  number: number
  gate: Gate
  onUnlock: () => void
}) {
  const copy = gateCopy(gate)
  return (
    <div style={styles.wrap}>
      <Cover url={coverUrl} />
      <div style={styles.card}>
        <span style={styles.number}>
          {t.episodeLabel} {number}
        </span>
        <h3 style={styles.title}>{copy.title}</h3>
        <p style={styles.body}>{copy.body}</p>
        <button style={styles.action} onClick={onUnlock}>
          {copy.action}
        </button>
      </div>
    </div>
  )
}

function gateCopy(gate: Gate) {
  switch (gate) {
    case 'need_channel':
      return {
        title: t.gate.needChannelTitle,
        body: t.gate.needChannelBody,
        action: t.gate.needChannelAction,
      }
    case 'need_bot':
      return { title: t.gate.needBotTitle, body: t.gate.needBotBody, action: t.gate.needBotAction }
    case 'need_app':
      return { title: t.gate.needAppTitle, body: t.gate.needAppBody, action: t.gate.needAppAction }
    default:
      return { title: '', body: '', action: '' }
  }
}

const styles = {
  wrap: {
    position: 'absolute',
    inset: 0,
    background: '#000',
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
  },
  cover: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: 0.35,
  },
  number: {
    position: 'relative',
    fontSize: 13,
    color: 'var(--text-dim)',
    letterSpacing: '.06em',
  },
  card: {
    position: 'relative',
    display: 'grid',
    justifyItems: 'center',
    gap: 10,
    padding: '26px 22px',
    margin: '0 24px',
    borderRadius: 18,
    // 不用 backdrop-filter：它要逐帧模糊身后那一层，而身后是正在解码的视频，
    // 低端安卓上这一项足以让滑动掉帧。纯色压暗看上去差不多。
    background: 'rgba(0,0,0,.72)',
    textAlign: 'center',
  },
  title: { margin: 0, fontSize: 17 },
  body: { margin: 0, fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.6 },
  action: {
    marginTop: 4,
    padding: '11px 26px',
    borderRadius: 100,
    background: 'var(--accent-gradient)',
    color: '#fff',
    fontSize: 14,
  },
} satisfies Record<string, React.CSSProperties>
